import React, { useState, useRef } from 'react'
import { useMutation, useQuery, gql } from '@apollo/client'
import { validateFuncForRider } from '../../constraints/constraints'
import { withTranslation } from 'react-i18next'
// core components
import {
  createRider,
  editRider,
  getRiders,
  getZones,
  getAvailableRiders
} from '../../apollo'
import useStyles from './styles'
import useGlobalStyles from '../../utils/globalStyles'
import {
  Box,
  Switch,
  Typography,
  Input,
  Alert,
  Select,
  MenuItem,
  Button,
  IconButton
} from '@mui/material'
import InputAdornment from '@mui/material/InputAdornment'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import TwoWheelerOutlinedIcon from '@mui/icons-material/TwoWheelerOutlined'
import FormCardHeader from '../FormCardHeader'

const CREATE_RIDER = gql`
  ${createRider}
`
const EDIT_RIDER = gql`
  ${editRider}
`
const GET_RIDERS = gql`
  ${getRiders}
`
const GET_ZONES = gql`
  ${getZones}
`
const GET_AVAILABLE_RIDERS = gql`
  ${getAvailableRiders}
`

function Rider(props) {
  const formRef = useRef()
  const mutation = props.rider ? EDIT_RIDER : CREATE_RIDER
  const name = props.rider ? props.rider.name : ''
  const userName = props.rider ? props.rider.username : ''
  const password = props.rider ? props.rider.password : ''
  const phone = props.rider ? props.rider.phone : ''
  const zone = props.rider && props.rider.zone ? props.rider.zone._id : ''
  // Legacy riders stored a bare accountNumber before bankDetails existed
  const bank = (props.rider && props.rider.bankDetails) || {}
  const [bankName, setBankName] = useState(bank.bankName || '')
  const [sortCode, setSortCode] = useState(bank.sortCode || '')
  const [accountNumber, setAccountNumber] = useState(
    bank.accountNumber || (props.rider ? props.rider.accountNumber || '' : '')
  )
  const [accountHolderName, setAccountHolderName] = useState(
    bank.accountHolderName || ''
  )
  const [mainError, mainErrorSetter] = useState('')
  const [success, successSetter] = useState('')
  const [nameError, nameErrorSetter] = useState(null)
  const [usernameError, usernameErrorSetter] = useState(null)
  const [passwordError, passwordErrorSetter] = useState(null)
  const [phoneError, phoneErrorSetter] = useState(null)
  const [userNameErrorMessage, setUserNameErrorMessage] = useState('')
  const [zoneError, zoneErrorSetter] = useState(null)
  const [showPassword, setShowPassword] = useState(false)
  const [riderAvailable, setRiderAvailable] = useState(
    props.rider ? props.rider.available : true
  )
  const [riderZone, setRiderZone] = useState(
    props.rider && props.rider.zone ? props.rider.zone._id : ''
  )

  const onCompleted = data => {
    if (!props.rider) clearFields()
    const message = props.rider
      ? t('RiderUpdatedSuccessfully')
      : t('RiderAddedSuccessfully')
    mainErrorSetter('')
    successSetter(message)
    setTimeout(hideAlert, 3000)
  }
  const onError = ({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      mainErrorSetter(graphQLErrors[0].message)
    }
    if (networkError) {
      mainErrorSetter(networkError.result.errors[0].message)
    }
    successSetter('')
    setTimeout(hideAlert, 3000)
  }
  const [mutate, { loading }] = useMutation(mutation, {
    refetchQueries: [{ query: GET_RIDERS }, { query: GET_AVAILABLE_RIDERS }],
    onError,
    onCompleted
  })
  const { data } = useQuery(GET_ZONES)

  const onBlur = (setter, field, state) => {
    const validationResult = validateFuncForRider({ [field]: state }, field)
    setter(validationResult.isValid)
  }
  const onSubmitValidaiton = () => {
    const nameError = validateFuncForRider(
      { name: formRef.current['input-name'].value },
      'name'
    )
    const usernameError = validateFuncForRider(
      { username: formRef.current['input-userName'].value },
      'username'
    )
    const passwordError = validateFuncForRider(
      { password: formRef.current['input-password'].value },
      'password'
    )
    const phoneError = validateFuncForRider(
      { phone: formRef.current['input-phone'].value },
      'phone'
    )
    const zoneError = validateFuncForRider(
      { zone: formRef.current['input-zone'].value },
      'zone'
    )

    nameErrorSetter(nameError.isValid)
    usernameErrorSetter(usernameError.isValid)
    setUserNameErrorMessage(usernameError.errorMessage)
    phoneErrorSetter(phoneError.isValid)
    passwordErrorSetter(passwordError.isValid)
    zoneErrorSetter(zoneError.isValid)
    return (
      nameError.isValid &&
      usernameError.isValid &&
      phoneError.isValid &&
      passwordError.isValid &&
      zoneError.isValid
    )
  }
  const clearFields = () => {
    formRef.current.reset()
    setBankName('')
    setSortCode('')
    setAccountNumber('')
    setAccountHolderName('')
    nameErrorSetter(null)
    usernameErrorSetter(null)
    passwordErrorSetter(null)
    phoneErrorSetter(null)
    zoneErrorSetter(null)
  }

  const hideAlert = () => {
    mainErrorSetter('')
    successSetter('')
  }
  const { t } = props

  const classes = useStyles()
  const globalClasses = useGlobalStyles()
  const isEdit = Boolean(props.rider)
  const inputClass = error => [
    globalClasses.input,
    error === false
      ? globalClasses.inputError
      : error === true
      ? globalClasses.inputSuccess
      : ''
  ]
  const field = (label, control, extra = null) => (
    <Box sx={{ minWidth: 0 }}>
      <Typography className={classes.labelText}>{label}</Typography>
      {control}
      {extra}
    </Box>
  )
  const onSave = e => {
    e.preventDefault()
    if (!onSubmitValidaiton()) return
    mutate({
      variables: {
        riderInput: {
          _id: props.rider ? props.rider._id : '',
          name: formRef.current['input-name'].value,
          username: formRef.current['input-userName'].value,
          password: formRef.current['input-password'].value,
          phone: formRef.current['input-phone'].value,
          zone: riderZone,
          available: riderAvailable,
          bankDetails: {
            bankName: bankName.trim(),
            sortCode: sortCode.trim(),
            accountNumber: accountNumber.trim(),
            accountHolderName: accountHolderName.trim()
          }
        }
      }
    })
    // Close the edit modal once the result message has been seen
    setTimeout(() => {
      if (typeof props.onClose === 'function') props.onClose()
    }, 4000)
  }

  return (
    <Box className={classes.container}>
      <FormCardHeader
        className={classes.heading}
        icon={TwoWheelerOutlinedIcon}
        title={isEdit ? t('EditRider') : t('AddRider')}
        subtitle={
          isEdit ? null : t('Create a rider account and assign a zone.')
        }
        onClose={props.onClose}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              {t('Available')}
            </Typography>
            <Switch
              checked={riderAvailable}
              onChange={() => setRiderAvailable(!riderAvailable)}
              id="input-available"
              name="input-available"
            />
          </Box>
        }
      />
      <Box className={classes.form}>
        <form ref={formRef} onSubmit={onSave}>
          <Box className={classes.grid}>
            {field(
              t('Name'),
              <Input
                id="input-name"
                name="input-name"
                placeholder={t('PHRiderName')}
                type="text"
                defaultValue={name}
                onBlur={event => {
                  onBlur(nameErrorSetter, 'name', event.target.value)
                }}
                disableUnderline
                className={inputClass(nameError)}
              />
            )}
            {field(
              t('Username'),
              <Input
                id="input-username"
                name="input-userName"
                placeholder={t('Username')}
                type="text"
                defaultValue={userName}
                onBlur={event =>
                  onBlur(usernameErrorSetter, 'username', event.target.value)
                }
                disableUnderline
                className={inputClass(usernameError)}
              />,
              usernameError === false ? (
                <Typography sx={{ color: 'error.main', fontSize: 12, mt: 0.5 }}>
                  {t(userNameErrorMessage)}
                </Typography>
              ) : null
            )}
            {field(
              t('Password'),
              <Input
                id="input-password"
                name="input-password"
                placeholder={t('Password')}
                type={showPassword ? 'text' : 'password'}
                defaultValue={password}
                onBlur={event =>
                  onBlur(passwordErrorSetter, 'password', event.target.value)
                }
                disableUnderline
                className={inputClass(passwordError)}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      edge="end"
                      aria-label={t('Toggle password visibility')}
                      onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? (
                        <VisibilityIcon fontSize="small" />
                      ) : (
                        <VisibilityOffIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
              />
            )}
            {field(
              t('Number'),
              <Input
                id="input-phone"
                name="input-phone"
                placeholder={t('PhoneNumber')}
                type="tel"
                defaultValue={phone}
                onBlur={event =>
                  onBlur(phoneErrorSetter, 'phone', event.target.value)
                }
                disableUnderline
                className={inputClass(phoneError)}
              />
            )}
            {field(
              t('Zone'),
              <Select
                id="input-zone"
                name="input-zone"
                displayEmpty
                value={riderZone}
                onChange={e => setRiderZone(e.target.value)}
                className={inputClass(zoneError)}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '& .MuiSelect-select': { pl: 0 }
                }}
                renderValue={value => {
                  const selected = data?.zones?.find(z => z._id === value)
                  return selected ? (
                    selected.title
                  ) : (
                    <Box component="span" sx={{ color: 'text.disabled' }}>
                      {t('RiderZone')}
                    </Box>
                  )
                }}>
                {data &&
                  data.zones.map(zone => (
                    <MenuItem value={zone._id} key={zone._id}>
                      {zone.title}
                    </MenuItem>
                  ))}
              </Select>
            )}
          </Box>

          <Box className={classes.section}>
            <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
              {t('BankDetails')}
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'text.disabled' }}>
              {t("Used to pay out the rider's earnings.")}
            </Typography>
          </Box>
          <Box className={classes.grid}>
            {field(
              t('BankName'),
              <Input
                id="input-bankName"
                name="input-bankName"
                placeholder={t('BankName')}
                type="text"
                value={bankName}
                onChange={event => setBankName(event.target.value)}
                disableUnderline
                className={globalClasses.input}
              />
            )}
            {field(
              t('SortCode'),
              <Input
                id="input-sortCode"
                name="input-sortCode"
                placeholder={t('SortCodePlaceholder')}
                type="text"
                value={sortCode}
                onChange={event => setSortCode(event.target.value)}
                disableUnderline
                className={globalClasses.input}
              />
            )}
            {field(
              t('AccountNumber'),
              <Input
                id="input-accountNumber"
                name="input-accountNumber"
                placeholder={t('AccountNumber')}
                type="text"
                value={accountNumber}
                onChange={event => setAccountNumber(event.target.value)}
                disableUnderline
                className={globalClasses.input}
              />
            )}
            {field(
              t('AccountHolderName'),
              <Input
                id="input-accountHolderName"
                name="input-accountHolderName"
                placeholder={t('AccountHolderName')}
                type="text"
                value={accountHolderName}
                onChange={event => setAccountHolderName(event.target.value)}
                disableUnderline
                className={globalClasses.input}
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              type="submit"
              className={globalClasses.button}
              sx={{ m: '0 !important', px: '36px !important' }}
              disabled={loading}>
              {isEdit ? t('Update') : t('Save rider')}
            </Button>
          </Box>
        </form>
        {success || mainError ? (
          <Box mt={2}>
            {success && (
              <Alert
                className={globalClasses.alertSuccess}
                variant="filled"
                severity="success">
                {success}
              </Alert>
            )}
            {mainError && (
              <Alert
                className={globalClasses.alertError}
                variant="filled"
                severity="error">
                {mainError}
              </Alert>
            )}
          </Box>
        ) : null}
      </Box>
    </Box>
  )
}
export default withTranslation()(Rider)
